import { CartRecap, CustomPageTitle, QuantityPicker } from '@components';
import { Button, Container, Icon, Input, Modal, Typography } from '@design-system';
import { DEFAULT_IMAGE, formatUnit } from '@utils';
import React from 'react';
import { FaShoppingBasket } from 'react-icons/fa';
import { HiMiniMagnifyingGlass } from 'react-icons/hi2';
import { IoMdClose } from 'react-icons/io';
import { IoBagHandleSharp } from 'react-icons/io5';
import { useParams } from 'react-router-dom';
import './GenericProductDisplayer.scss';
import { GenericProductSkeleton } from './GenericProductSkeleton';
import { useGenericProductDisplayer } from './useGenericProductDisplayer';

type GenericProductDisplayerProps = {
    type: 'appro' | 'epi';
    basePath: string;
};

export const GenericProductDisplayer: React.FC<GenericProductDisplayerProps> = ({ type }) => {
    const { id } = useParams();
    const {
        isLoading,
        errorMessage,
        toTitleCase,
        categoryTitle,
        showModal,
        setShowModal,
        quantity,
        setQuantity,
        items,
        handleBasketClick,
        handleValidateQuantity,
        searchTerm,
        setSearchTerm,
        filteredProducts
    } = useGenericProductDisplayer(id, type);

    if (errorMessage) {
        return (
            <Container>
                <Typography intrinsic="p" color="red">
                    {errorMessage}
                </Typography>
            </Container>
        );
    }

    return (
        <>
            {!isLoading && categoryTitle && (
                <CustomPageTitle>
                    <Typography
                        intrinsic="h3"
                        iconColor={type === 'appro' ? 'red' : 'blue'}
                        icon={<IoBagHandleSharp />}
                        iconFilled
                        iconSize={20}
                    >
                        {categoryTitle}
                    </Typography>
                </CustomPageTitle>
            )}

            <Container className="search-container" padding="medium large">
                <Input
                    placeholder=""
                    value={searchTerm}
                    onValueChange={setSearchTerm}
                    className="search-input"
                    icon={<Icon icon={<HiMiniMagnifyingGlass />} size={25} color="grey" />}
                    iconPosition="left"
                />
            </Container>

            {isLoading ? (
                <GenericProductSkeleton />
            ) : (
                <Container className="generic-products" padding="medium" column>
                    <Container className="products-container" padding="small" column>
                        {filteredProducts.map((product) => (
                            <Container
                                key={product.id}
                                className="product-card"
                                padding="small-medium"
                                flexRow
                                style={{
                                    animation: 'fadeIn 0.3s ease-in-out'
                                }}
                            >
                                <div className="product-container">
                                    <img src={product.image ?? DEFAULT_IMAGE} alt={product.label} className="product-image" />

                                    <div className="product-info">
                                        <Typography intrinsic="h5" color="marine" className="product-label">
                                            {toTitleCase(product.label)}
                                        </Typography>
                                        <Typography intrinsic="p" variant="secondary" className="product-unit">
                                            {formatUnit(product.packing_unit)}
                                        </Typography>
                                    </div>
                                </div>

                                <div className="product-quantity-container">
                                    <Icon
                                        icon={<FaShoppingBasket />}
                                        size={24}
                                        variant="surrounded"
                                        color="green"
                                        bordered
                                        onClick={() => handleBasketClick(product)}
                                    />
                                    {items.find((item) => item.id === product.id) && (
                                        <span className="product-quantity">{items.find((item) => item.id === product.id)?.quantity}</span>
                                    )}
                                </div>
                            </Container>
                        ))}
                    </Container>
                </Container>
            )}

            <Modal
                title="Choisir la quantité"
                open={showModal}
                setClose={() => setShowModal(false)}
                footer={
                    <div className="modal-footer">
                        <Button
                            color="grey"
                            variant="tertiary"
                            onClick={() => setShowModal(false)}
                            icon={<IoMdClose />}
                            iconPosition="right"
                            iconSize={20}
                        >
                            <Typography intrinsic="h4">Annuler</Typography>
                        </Button>
                        <Button color="green" size="small" variant="secondary" onClick={handleValidateQuantity}>
                            <Typography intrinsic="h4">Valider</Typography>
                        </Button>
                    </div>
                }
            >
                <Container padding="medium" center>
                    <QuantityPicker quantity={quantity} onQuantityChange={setQuantity} />
                </Container>
            </Modal>

            <CartRecap items={items} type={type} />
        </>
    );
};
